/**
 * Spatius AvatarKit Backend Mode 接入。
 *
 * 仅处理 actionType === 'robot_realtime' 且 config.provider === 'spatius.ai'。
 * 前端不连 Motion Server、不需要 session token：
 * 初始化 SDK(backend) → load → AvatarView → 连自家 WS → audio_url → yield 音频/动作帧。
 */

import { SpatiusBackendClient } from '@/util/spatiusBackendClient'

export const SPATIUS_PROVIDER = 'spatius.ai'
export const REALTIME_AVATAR_ACTION_TYPE = 'robot_realtime'

const mountedViews = new Map()
let sdkModulePromise = null
let initializedAppId = null
let backendClient = null

function loadSdk() {
  if (!sdkModulePromise) {
    sdkModulePromise = import('@spatius/avatarkit')
  }
  return sdkModulePromise
}

export function getRealtimeProvider(action) {
  const cfg = action && action.config
  return (cfg && cfg.provider) || ''
}

export function isRealtimeAvatarAction(action) {
  return !!(
    action &&
    action.actionType === REALTIME_AVATAR_ACTION_TYPE &&
    getRealtimeProvider(action) === SPATIUS_PROVIDER
  )
}

function pickField(sources, keys) {
  const keyList = Array.isArray(keys) ? keys : [keys]
  for (let i = 0; i < sources.length; i++) {
    const src = sources[i]
    if (!src) continue
    for (let k = 0; k < keyList.length; k++) {
      const value = src[keyList[k]]
      if (value) return value
    }
  }
  return ''
}

export function resolveSpatiusConfig(lesson, action) {
  const providerCfg = (action && action.config) || {}
  const lessonCfg = (lesson && (lesson.spatius || (lesson.config && lesson.config.spatius))) || {}
  const region = pickField([providerCfg, action, lessonCfg], ['region'])
  return {
    provider: pickField([providerCfg, action, lessonCfg], ['provider']),
    appId: pickField([providerCfg, action, lessonCfg], ['app_id', 'appId']),
    avatarId: pickField([providerCfg, action, lessonCfg], ['avatar_id', 'avatarId']),
    region: region === 'auto' ? '' : region
  }
}

export function collectRealtimeAvatarActions(lesson) {
  const list = []
  if (!lesson || !lesson.scenes) return list
  for (const scene of lesson.scenes) {
    for (const group of (scene.groups || [])) {
      for (const action of (group.actions || [])) {
        if (isRealtimeAvatarAction(action)) list.push(action)
      }
    }
  }
  return list
}

export async function ensureSpatiusInitialized(config) {
  const appId = config && config.appId
  if (!appId) {
    throw new Error('Spatius 缺少 app_id')
  }
  const sdk = await loadSdk()
  if (initializedAppId === appId) return sdk
  const initOptions = {
    drivingServiceMode: sdk.DrivingServiceMode.backend,
    logLevel: sdk.LogLevel.warning,
    audioFormat: { channelCount: 1, sampleRate: 16000 }
  }
  if (config.region) initOptions.region = config.region
  await sdk.AvatarSDK.initialize(appId, initOptions)
  initializedAppId = appId
  return sdk
}

function ensureBackendClient() {
  if (backendClient) return backendClient
  backendClient = new SpatiusBackendClient({
    onError: (message) => {
      console.error('[spatius] backend error', message)
    },
    onClosed: () => {
      backendClient = null
    }
  })
  return backendClient
}

async function ensureBackendConnected() {
  const client = ensureBackendClient()
  if (!client.isOpen) {
    await client.connect()
  }
  return client
}

export async function preloadRealtimeAvatars(lesson, onOneDone) {
  const actions = collectRealtimeAvatarActions(lesson)
  const unique = []
  const seen = {}
  for (const action of actions) {
    const config = resolveSpatiusConfig(lesson, action)
    if (!config.appId || !config.avatarId) {
      console.warn('[spatius] 跳过预加载，缺少 app_id 或 avatar_id', action)
      if (onOneDone) onOneDone()
      continue
    }
    const key = config.appId + '::' + config.avatarId
    if (seen[key]) {
      if (onOneDone) onOneDone()
      continue
    }
    seen[key] = true
    unique.push(config)
  }
  if (!unique.length) return
  for (const config of unique) {
    try {
      const sdk = await ensureSpatiusInitialized(config)
      await sdk.AvatarManager.shared.load(config.avatarId, (info) => {
        if (info && info.type === 'failed') {
          console.error('[spatius] 预加载失败', info.error)
        }
      })
    } catch (err) {
      console.error('[spatius] 预加载异常', err)
    }
    if (onOneDone) onOneDone()
  }
}

export async function mountRealtimeAvatar(elementId, container, lesson, action) {
  disposeRealtimeAvatar(elementId)
  const config = resolveSpatiusConfig(lesson, action)
  if (!config.appId || !config.avatarId) {
    throw new Error('实时数字人缺少 app_id 或 avatar_id')
  }
  container.innerHTML = ''
  const loading = document.createElement('div')
  loading.className = 'spatius-avatar-loading'
  loading.textContent = '数字人加载中...'
  container.appendChild(loading)

  const sdk = await ensureSpatiusInitialized(config)
  const avatar = await sdk.AvatarManager.shared.load(config.avatarId)
  if (!avatar) {
    throw new Error('AvatarManager.load 未返回形象')
  }

  loading.remove()
  const view = new sdk.AvatarView(avatar, container)
  const handle = {
    view,
    controller: view.controller,
    config,
    audioReady: false,
    speaking: false
  }
  view.onFirstRendering = () => {
    console.log('[spatius] first frame', config.avatarId)
  }
  view.controller.onError = (error) => {
    console.error('[spatius] controller error', error && error.code, error && error.message)
  }
  mountedViews.set(elementId, handle)

  try {
    const client = await ensureBackendConnected()
    client.setController(view.controller)
    client.setAvatar(config.avatarId)
  } catch (err) {
    console.warn('[spatius] 后端 WS 暂未连上，稍后再试', err)
  }
  return handle
}

export async function prepareSpatiusUserGesture() {
  const ids = Array.from(mountedViews.keys())
  for (let i = 0; i < ids.length; i++) {
    const handle = mountedViews.get(ids[i])
    if (!handle || handle.audioReady) continue
    try {
      await handle.controller.initializeAudioContext()
      handle.audioReady = true
    } catch (err) {
      console.warn('[spatius] initializeAudioContext 失败', err)
    }
  }
}

function waitConversationIdle(controller, timeoutMs) {
  return new Promise((resolve) => {
    let settled = false
    let seenPlaying = false
    const prev = controller.onConversationState
    const finish = () => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      controller.onConversationState = prev
      resolve()
    }
    const timer = window.setTimeout(finish, timeoutMs)
    controller.onConversationState = (state) => {
      if (typeof prev === 'function') prev(state)
      if (state === 'playing') seenPlaying = true
      if (state === 'idle' && seenPlaying) finish()
    }
  })
}

function playHtmlAudio(audioEl) {
  return new Promise((resolve) => {
    if (!audioEl) {
      resolve()
      return
    }
    const done = () => {
      audioEl.removeEventListener('ended', done)
      audioEl.removeEventListener('error', done)
      resolve()
    }
    audioEl.addEventListener('ended', done)
    audioEl.addEventListener('error', done)
    const playRet = audioEl.play()
    if (playRet && playRet.catch) {
      playRet.catch((err) => {
        console.error('[spatius] HTML 音频播放失败', err)
        done()
      })
    }
  })
}

export async function speakRealtimeAvatar(elementId, audioUrl, audioEl, volume) {
  const handle = mountedViews.get(elementId)
  if (!handle) {
    await playHtmlAudio(audioEl)
    return
  }
  if (volume != null && handle.controller.setVolume) {
    handle.controller.setVolume(Math.max(0, Math.min(1, Number(volume) / 100)))
  }
  try {
    if (!handle.audioReady) {
      await handle.controller.initializeAudioContext()
      handle.audioReady = true
    }
    const client = await ensureBackendConnected()
    client.setController(handle.controller)
    if (handle.config.avatarId) client.setAvatar(handle.config.avatarId)
    if (!audioUrl) {
      await playHtmlAudio(audioEl)
      return
    }
    handle.speaking = true
    client.playAudioUrl(audioUrl)
    await waitConversationIdle(handle.controller, 180000)
    handle.speaking = false
  } catch (err) {
    console.warn('[spatius] Backend Mode 对口型失败，改用普通音频', err)
    handle.speaking = false
    if (audioEl && volume != null) {
      audioEl.volume = Math.max(0, Math.min(1, Number(volume) / 100))
    }
    await playHtmlAudio(audioEl)
  }
}

export function pauseRealtimeAvatar(elementId) {
  const handle = mountedViews.get(elementId)
  if (!handle) return
  try { handle.controller.pause() } catch (e) { /* ignore */ }
}

export async function resumeRealtimeAvatar(elementId) {
  const handle = mountedViews.get(elementId)
  if (!handle) return
  try { await handle.controller.resume() } catch (e) { /* ignore */ }
}

export function disposeRealtimeAvatar(elementId) {
  const handle = mountedViews.get(elementId)
  if (!handle) return
  try {
    if (backendClient) {
      if (backendClient.controller === handle.controller) {
        backendClient.interrupt()
        backendClient.setController(null)
      }
    }
  } catch (e) { /* ignore */ }
  try {
    if (handle.view && handle.view.dispose) handle.view.dispose()
  } catch (e) { /* ignore */ }
  mountedViews.delete(elementId)
}

export function disposeAllRealtimeAvatars() {
  Array.from(mountedViews.keys()).forEach(disposeRealtimeAvatar)
}

export function cleanupSpatiusSdk() {
  disposeAllRealtimeAvatars()
  if (backendClient) {
    backendClient.close()
    backendClient = null
  }
  if (!sdkModulePromise || !initializedAppId) {
    initializedAppId = null
    return
  }
  sdkModulePromise.then((sdk) => {
    try { sdk.AvatarSDK.cleanup() } catch (e) { /* ignore */ }
  }).catch(() => {})
  initializedAppId = null
  sdkModulePromise = null
}
